import React, { useMemo, useState } from 'react';
import { Accordion, Button, Carousel, Modal, Select, TextField, Spinner } from '@cimpress/react-components';
import { connect } from 'react-redux';
import { ENTER } from 'src/util/keycodes';
import type { RootState } from 'src/store';
import ProductSelector from './components/productSelector';
import getSceneVariation from './selectors/getSceneVariation';
import { loadPreviews } from './slice';
import getPreviewScenes from './selectors/getPreviews';

interface PurposeOption {
    label: string;
    value: string;
}

interface Props {
    sku?: string;
    skuVersion?: number;
    previews?: {
        loadingPreviews: boolean;
        isReady?: boolean;
        images?: string[];
        error?: string;
    };
    purposes: PurposeOption[];
    loadPreviews: (payload: { attributes: Record<string, any>; purposes: PurposeOption[] }) => void;
    enable: boolean;
}

const PreviewScene = (props: Props) => {
    const [open, setOpen] = useState(false);
    const [error, setError] = useState('');
    const [purposes, setPurposes] = useState<PurposeOption[]>([]);
    const [customPurpose, setCustomPurpose] = useState('');
    const [zoomImage, setZoomImage] = useState<string | boolean | undefined>(false);
    const { images, loadingPreviews } = props.previews || {};
    const [attributes, setAttributes] = useState<Record<string, any>>({});
    const handleChange = (attrs: Record<string, any>) => {
        setAttributes(attrs);
    };
    const handleToggle = () => {
        setOpen((o) => !o);
    };
    const handleClick = () => {
        if (purposes.length > 0) {
            setError('');
            props.loadPreviews({ attributes, purposes });
        } else {
            setError('Please select at least one Purpose to Preview');
        }
    };
    const onZoomImage = (currentSlide: number) => {
        // Only ever wired up to the Carousel below, which itself only renders once `images` is set.
        setZoomImage((images as string[])[currentSlide]);
    };
    const closeModal = () => {
        setZoomImage(undefined);
    };

    const addPurpose = (selectedOptions: PurposeOption[]) => {
        if (selectedOptions.length > 0 && error) {
            setError('');
        }

        setPurposes(selectedOptions || []);
    };
    const updateCurrentSubpurpose = (e: React.ChangeEvent<HTMLInputElement> & { isValid: boolean }) => {
        setCustomPurpose(e.target.value);
    };
    const addCustomSubpurposes = () => {
        setPurposes((e) => [
            ...e,
            { label: `Merchandising ${customPurpose}`, value: `merchandising:${customPurpose}` },
        ]);
    };

    const renderImages = useMemo(
        () =>
            images && images.length > 0 ? (
                <Carousel size='s' showZoomOverlay={true} onCarouselClick={onZoomImage}>
                    {images.map((image) => (
                        <img key={image} src={image} alt={`Scene Preview for ${image}`} />
                    ))}
                </Carousel>
            ) : (
                <></>
            ),
        [images],
    );

    const purposeOptions = useMemo(
        () => props.purposes.filter((p) => !purposes.find((i) => i.value === p.value)),
        [purposes],
    );

    if (loadingPreviews) {
        // `isCenter`/`show` aren't real `SpinnerProps` (see every other `<Spinner />` usage in this
        // codebase) -- they were always inert attributes spread onto the DOM node, so dropping them
        // doesn't change what renders.
        return <Spinner />;
    }

    if (!props.enable) {
        return <></>;
    }

    return (
        <Accordion title='Preview' defaultOpen={open} onHeaderClick={handleToggle}>
            <div className='wrapper-preview-scene'>
                <ProductSelector
                    attributes={attributes}
                    sku={props.sku}
                    skuVersion={props.skuVersion}
                    onChange={handleChange}
                />
                <Select
                    label='Select Purpose(s)'
                    isMulti={true}
                    value={purposes}
                    options={purposeOptions}
                    onChange={addPurpose}
                    menuPortalTarget={document.body}
                    helpText={error}
                    containerClassName={error ? 'has-error' : ''}
                />
                <TextField
                    value={customPurpose}
                    label='Add custom merchandising subpurposes'
                    onChange={updateCurrentSubpurpose}
                    onKeyDown={(e) => {
                        if (e.keyCode === ENTER) {
                            addCustomSubpurposes();
                        }
                    }}
                    rightAddon={
                        <Button onClick={addCustomSubpurposes} disabled={!customPurpose}>
                            Add
                        </Button>
                    }
                />
                <Button style={{ marginTop: '12px' }} variant='primary' onClick={handleClick}>
                    Show Preview
                </Button>
                <div className='wrapper-preview-scene-carousel'>{renderImages}</div>
                <Modal
                    className='wrapper-preview-scene-modal'
                    size='lg'
                    closeButton={true}
                    show={!!zoomImage}
                    onRequestHide={closeModal}
                    closeOnOutsideClick={true}>
                    {/* `zoomImage` is only ever `false` while this Modal is hidden (`show={!!zoomImage}`),
                    so it's always a real image URL (or `undefined`) whenever this actually renders. */}
                    <img src={zoomImage as string | undefined} alt='Preview Scene' />
                </Modal>
            </div>
        </Accordion>
    );
};

function mapStateToProps(state: RootState) {
    const { sku, skuVersion, sceneVariableId, scenePurposes } = getSceneVariation(state);
    const previews = getPreviewScenes(state);
    return {
        sku,
        skuVersion,
        previews,
        purposes: scenePurposes,
        enable: !!sceneVariableId,
    };
}

export default connect(mapStateToProps, {
    loadPreviews,
})(PreviewScene);
